import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useLocation } from "@remix-run/react"

import { cn } from "@core/lib/utils"
import { t } from "i18next"

/**
 * Returns true when the current route is an admin route (`/admin/...`).
 * Admin routes keep the original light shadcn dialog styling; everything
 * else gets the SPG dark theme.
 */
function useIsAdminRoute(): boolean {
  // useLocation can be called outside a Router during SSR edge cases; guard.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pathname } = useLocation();
    return pathname?.toLowerCase().startsWith("/admin") ?? false;
  } catch {
    return false;
  }
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const isAdmin = useIsAdminRoute();
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        isAdmin
          ? "fixed inset-0 z-[300] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          : "fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onPointerDownOutside, onInteractOutside, onEscapeKeyDown, ...props }, ref) => {
  const isAdmin = useIsAdminRoute();
  return (
    <DialogPortal>
      <DialogOverlay>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            isAdmin
              ? // Original shadcn light dialog (admin)
                "fixed left-[50%] top-[50%] z-[301] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6 border border-neutral-200 bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-neutral-800 dark:bg-neutral-950"
              : // SPG dark themed dialog (storefront)
                "fixed left-[50%] top-[50%] z-[301] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6 border rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.55)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] bg-[#151b26] border-white/10 text-white",
            className
          )}
          {...props}
          onPointerDownOutside={(event) => {
            onPointerDownOutside?.(event);
            event.preventDefault();
          }}
          onInteractOutside={(event) => {
            onInteractOutside?.(event);
            event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            onEscapeKeyDown?.(event);
            event.preventDefault();
          }}
        >
          <div className="max-h-[90dvh] overflow-y-auto -webkit-overflow-scrolling-touch scrollbar-hidden p-1">
            {children}
            <DialogPrimitive.Close
              className={cn(
                "absolute right-4 top-4 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none",
                isAdmin
                  ? "opacity-70 ring-offset-white hover:opacity-100 focus:ring-neutral-950 data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-500 dark:ring-offset-neutral-950 dark:focus:ring-neutral-300 dark:bg-neutral-800 dark:text-neutral-400"
                  : "text-white/60 hover:text-white focus:ring-[#EFBD45] focus:ring-offset-[#151b26]"
              )}
            >
              <X className="h-4 w-4 !m-1" />
              <span className="sr-only">{t("ui.close", "Close")}</span>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const isAdmin = useIsAdminRoute();
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        isAdmin
          ? "text-lg font-semibold leading-none tracking-tight text-slate-800 dark:text-slate-200"
          : "leading-none tracking-wider text-2xl font-['Bebas_Neue',sans-serif] text-[#EFBD45]",
        className
      )}
      {...props}
    />
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const isAdmin = useIsAdminRoute();
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(
        isAdmin
          ? "text-sm text-neutral-500 dark:text-neutral-400"
          : "text-sm text-white/60 font-['Outfit',sans-serif]",
        className
      )}
      {...props}
    />
  );
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
